import type { TranslateFn } from '@/components/ui/embedded-resource-table/types';
import type { ToolbarFilterOption } from '@/components/ui/generic-toolbar/GenericToolbar';

/**
 * A colleague as the filter builders need them.
 *
 * `roles` is what ReferenceDataCache::usersForSelect() ships; `role` and the
 * nested `roles` objects are what `/api/users/assignable` and Inertia's `User`
 * type carry. All three are accepted so a caller does not have to normalise.
 */
export interface AssignableUser {
    id?: string;
    uuid?: string;
    first_name?: string | null;
    last_name?: string | null;
    role?: string | null;
    roles?: Array<string | { name: string }> | null;
}

/**
 * Role buckets for an assignee filter, most-likely-wanted first.
 *
 * A colleague lands in the first bucket whose role they hold, so someone who is
 * both a service manager and an engineer is filed under the more senior of the
 * two rather than appearing twice. Everyone unmatched falls into a trailing
 * "everyone else" bucket.
 */
const ROLE_GROUPS: ReadonlyArray<{ labelKey: string; labelFallback: string; roles: readonly string[] }> = [
    { labelKey: 'projects.index.filters.assigned_groups.sales', labelFallback: 'Sales managers', roles: ['Sales Manager'] },
    { labelKey: 'projects.index.filters.assigned_groups.administrators', labelFallback: 'Administrators', roles: ['Administrator'] },
    { labelKey: 'projects.index.filters.assigned_groups.project_managers', labelFallback: 'Project managers', roles: ['Project Manager'] },
    {
        labelKey: 'projects.index.filters.assigned_groups.service',
        labelFallback: 'Service managers & supervisors',
        roles: ['Service Manager', 'Service Supervisor'],
    },
    { labelKey: 'projects.index.filters.assigned_groups.engineers', labelFallback: 'Engineers', roles: ['Engineer'] },
];

/** Every role name a user holds, whatever shape the payload arrived in. */
function roleNames(user: AssignableUser): string[] {
    const fromList = (user.roles ?? []).map((role) => (typeof role === 'string' ? role : role?.name)).filter((name): name is string => Boolean(name));

    return user.role ? [...fromList, user.role] : fromList;
}

export function assignableUserId(user: AssignableUser): string {
    // The assignable-users endpoint selects `uuid`; the server reference lists map
    // it to `id`. Callers should not have to care which one they got.
    return user.id ?? user.uuid ?? '';
}

/**
 * Grouped, pre-sorted options for an assignee or created-by filter.
 *
 * `ToolbarFilterOption.group` headings are positional — FilterPanel starts a new
 * heading wherever the group changes — so the list has to arrive already ordered
 * by group. That ordering is the whole point of doing this in one place.
 */
export function buildAssignedUserOptions(users: AssignableUser[], t: TranslateFn): ToolbarFilterOption[] {
    const othersLabel = t('projects.index.filters.assigned_groups.others', 'Everyone else');

    return users
        .map((user) => {
            const held = roleNames(user);
            const groupIndex = ROLE_GROUPS.findIndex((group) => group.roles.some((role) => held.includes(role)));

            return {
                value: assignableUserId(user),
                label: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                group: groupIndex === -1 ? othersLabel : t(ROLE_GROUPS[groupIndex].labelKey, ROLE_GROUPS[groupIndex].labelFallback),
                // Not part of the option shape — only used to order the list below.
                groupOrder: groupIndex === -1 ? ROLE_GROUPS.length : groupIndex,
            };
        })
        .filter((option) => option.value !== '' && option.label !== '')
        .sort((a, b) => a.groupOrder - b.groupOrder || a.label.localeCompare(b.label))
        .map(({ value, label, group }) => ({ value, label, group }));
}
